import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { EMPTY } from '@/lib/format'
import { cn } from '@/lib/utils'
import { SectionCard } from '@/components/common/SectionCard'

export function SectionHeading({ title, icon: Icon, className }: { title: ReactNode; icon?: LucideIcon; className?: string }) {
  return (
    <h2 className={cn('flex items-center gap-2 text-sm font-semibold', className)}>
      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      {title}
    </h2>
  )
}

export function Panel({
  title,
  icon: Icon,
  action,
  description,
  flush,
  className,
  bodyClassName,
  children
}: {
  title?: ReactNode
  icon?: LucideIcon
  action?: ReactNode
  description?: ReactNode
  flush?: boolean
  className?: string
  bodyClassName?: string
  children: ReactNode
}) {
  return (
    <SectionCard
      title={title}
      icon={Icon && <Icon />}
      action={action}
      description={description}
      flush={flush}
      className={className}
      bodyClassName={bodyClassName}
    >
      {children}
    </SectionCard>
  )
}

export function Metric({
  icon: Icon,
  label,
  value,
  hint,
  className
}: {
  icon?: LucideIcon
  label: string
  value: ReactNode
  hint?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('min-w-0 rounded-2xl border border-border bg-card p-3.5 shadow-xs sm:p-4', className)}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
        <span className="min-w-0 truncate">{label}</span>
      </div>
      <div className="tnum mt-1 break-words text-lg font-semibold sm:text-xl">{value}</div>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function InfoList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('space-y-2.5 text-sm', className)}>{children}</dl>
}

export function InfoRow({ label, value, mono, className }: { label: ReactNode; value?: ReactNode; mono?: boolean; className?: string }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <div className={cn('flex items-baseline justify-between gap-3', className)}>
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn('min-w-0 truncate text-right', mono && 'font-mono text-[12.5px]', empty && 'text-muted-foreground')}
        title={typeof value === 'string' ? value : undefined}
      >
        {empty ? EMPTY : value}
      </dd>
    </div>
  )
}

export function Meta({ icon: Icon, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
      <span className="truncate">{children}</span>
    </span>
  )
}

export function DetailSkeleton() {
  return (
    <div className="space-y-6 pb-4" aria-hidden>
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 animate-pulse rounded-2xl bg-muted" />
        <div className="space-y-2">
          <div className="h-6 w-56 animate-pulse rounded bg-muted" />
          <div className="h-3.5 w-80 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="h-24 animate-pulse rounded-2xl bg-muted" />
            <div className="h-24 animate-pulse rounded-2xl bg-muted" />
            <div className="h-24 animate-pulse rounded-2xl bg-muted" />
          </div>
          <div className="h-72 animate-pulse rounded-2xl bg-muted" />
        </div>
        <div className="space-y-3">
          <div className="h-56 animate-pulse rounded-2xl bg-muted" />
          <div className="h-40 animate-pulse rounded-2xl bg-muted" />
        </div>
      </div>
    </div>
  )
}
